/**
 * API client configuration for Favotrip Next.js frontend.
 *
 * Two backends exist:
 * - **BFF** (Backend-For-Frontend): the main API at api-new.favotrip.nl, paths
 *   root-relative (`/deals`, `/categories`, `/hotels/live-prices`, …).
 *   Used for ALL data fetching: deals, categories, vouchers, booking, payments,
 *   plus the newsletter subscription (`bff.post("/newsletter")`).
 *   IMPORTANT: The CMS API does NOT work reliably — always use BFF endpoints.
 *
 * - **CMS**: legacy CMS client. Removed 2026-09-24 (round 12, independent
 *   Opus 5.5 review on site-frontend PR #1470): had zero call-sites
 *   (confirmed via grep across src/ before removal), and its
 *   `NEXT_PUBLIC_CMS_URL` fallback was never part of the sandbox-override
 *   guard's covered set (check-staging-sandbox-overrides.mjs), so a
 *   sandbox build could never override it anyway. Flat pages and the
 *   newsletter go through the BFF. If a real CMS need reappears, add a
 *   new client with an env-driven base URL that IS in the guard's
 *   covered set, or an explicit, single-line-scoped allowlist entry in
 *   scripts/check-no-hardcoded-prod-hosts.mjs -- never a window-scoped one.
 *
 * The pre-migration BFF host is DEAD: permanent 502 since the April 2026 AWS
 * migration, nothing in production talks to it. It is named (and banned) in
 * scripts/no-dead-api-hosts.test.mjs. Leftover mentions of it, including a
 * fallback that used to sit on the now-removed CMS_URL line, cost a false P1
 * outage investigation on 2026-07-25.
 *
 * Environment variables (optional, defaults to production):
 *   NEW_BACKEND_URL      — internal backend URL, server-side only (e.g. http://favotrip-backend:3000)
 *   NEXT_PUBLIC_BFF_URL  — public BFF base URL (browser + build fallback)
 *   NEXT_PUBLIC_IMG_URL  — Image CDN base URL
 */
import axios from "axios";
import { resolveBffBaseUrl } from "./bff-url.mjs";
import { FRONTEND_SLEUTEL_HEADER, isBackendBestemming, serverFrontendSleutel } from "./frontend-sleutel";

/** BFF API client — primary data source for all deal/booking/voucher data */
// Server-side, hit the INTERNAL backend (NEW_BACKEND_URL) so data fetches go
// straight to the backend container instead of hair-pinning 3-4s out through
// Cloudflare and back to the same origin (that round-trip blew the 4s
// withBudget on /vouchers and emptied the grid on every TLD — Dennis
// 2026-06-24). The browser keeps the public URL. See bff-url.mjs. Same
// precedence the /api/booking/* route handlers already use.
const BFF_URL = resolveBffBaseUrl({
  isServer: typeof window === "undefined",
  internalUrl: process.env.NEW_BACKEND_URL,
  publicUrl: process.env.NEXT_PUBLIC_BFF_URL,
});

/** Primary BFF (backend-for-frontend) axios client at api-new.favotrip.nl, 10s timeout. Used for all deal/category/voucher/booking/payment data, and the newsletter. */
export const bff = axios.create({
  baseURL: BFF_URL,
  timeout: 10000,
});

// Server-side aanroepen dragen de frontend-sleutel, zodat de backend de website
// herkent en haar een eigen rem geeft (anders deelt zij 1.500/min met alle
// bezoekers, en een zoekrobot-golf na een uitrol geeft 429). Geen klant-IP: veel
// aanroepen zitten in unstable_cache, waar headers() niet mag. In de browser
// geeft serverFrontendSleutel() null en gaat er niets mee; een kop die de
// aanroeper zelf zet blijft staan.
// (`?.`: tests die axios.create wegmocken geven een client zonder interceptors.)
function bffBestemmingIsBackend(config) {
  try {
    return isBackendBestemming(bff.getUri(config), BFF_URL);
  } catch {
    return false;
  }
}

bff.interceptors?.request?.use((config) => {
  const sleutel = serverFrontendSleutel();
  // Alleen naar onze eigen backend: een absolute URL naar een andere origin
  // (axios negeert dan baseURL) krijgt de sleutel nooit. De bestemming komt van
  // axios zelf (getUri = buildFullPath incl. allowAbsoluteUrls), zodat controle
  // en echt verzoek nooit uiteenlopen (fixronde 2 #1909). Die uitkomst wordt
  // zonder basis geparst: een relatieve uitkomst of een baseURL met witruimte
  // krijgt nooit een sleutel (fixronde 3 #1909).
  if (
    sleutel &&
    config.headers &&
    bffBestemmingIsBackend(config) &&
    !config.headers.has?.(FRONTEND_SLEUTEL_HEADER)
  ) {
    config.headers.set(FRONTEND_SLEUTEL_HEADER, sleutel);
  }
  return config;
});

/** Base URL for deal/voucher images. Prefix deal.deals_img with this. */
export const IMG_BASE = process.env.NEXT_PUBLIC_IMG_URL || "https://images.prod.favotrip.nl";

/**
 * Backend origin. Uploaded assets (CMS `/uploads/**`: sfeerbeelden, campaign
 * graphics) are served from here, NOT from IMG_BASE — the images CDN does not
 * host `/uploads` and 404s such a path. Prefix relative `/uploads/...` paths
 * with this, `images/...` CDN paths with IMG_BASE. Same env default as
 * customer-api.js / fx-client.js so all three agree on the origin.
 */
export const API_ORIGIN =
  process.env.NEXT_PUBLIC_API_BASE_URL || "https://api.favotrip.nl";

/**
 * Resolve a deal/voucher image field (`deals_img`) to a usable <img> src.
 *
 * Most records store a path relative to IMG_BASE (e.g. `uploads/images/x.jpg`),
 * but some store an already-absolute origin URL (e.g.
 * `https://api.favotrip.nl/uploads/images/x.jpg`). Blindly prefixing IMG_BASE
 * to an absolute URL produced the malformed
 * `https://images.prod.favotrip.nl/https://api.favotrip.nl/...` which 404s and
 * shows a broken image (Dennis 2026-06-04, ING Italie card on /ing).
 *
 * Returns null for empty/invalid input so callers can apply their own
 * placeholder. The deal detail page already inlined this `startsWith("http")`
 * guard; this centralises it for every card/cross-sell/checkout surface.
 */
export function imgUrl(path, base = IMG_BASE) {
  if (!path || typeof path !== "string") return null;
  return /^https?:\/\//i.test(path) ? path : `${base}/${path.replace(/^\/+/, "")}`;
}
